import React, { useEffect, useState } from "react";
import { Button, Cascader, DatePicker, message, Space, Tabs } from "antd";
import dayjs from "dayjs";
import styles from "./index.module.scss";
import Service, { ElAdminEventLogService } from "../../../services";
import EventLogsTable from "../../../components/eventLogs/EventLogsTable";
import ModelEventLogsTable from "../../../components/eventLogs/ModelEventLogsTable";
import AccountEventLogsTable from "../../../components/eventLogs/AccountEventLogsTable";

const { RangePicker } = DatePicker;

const EventLogs = () => {
  const [groupNameToAccountsMap, setGroupNameToAccountsMap] = useState(new Map());
  const [isLoadingData, setLoadingData] = useState(false);
  const [selectedTradeAccounts, setSelectedTradeAccounts] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  const [rangePicker, setRangePicker] = useState([dayjs().subtract(7, "days"), dayjs()]);
  const [activeTab, setActiveTab] = useState("1");

  async function fetchTradeStatusAndPermissions() {
    try {
      const listManagedTradeAccountStatus = await Service.listManagedTradeAccountStatus();
      const groupNameToAccountsMap = new Map();
      listManagedTradeAccountStatus.forEach(({ groupName, parsedTradeAccountStatusList }) => {
        const accountSet = new Set(parsedTradeAccountStatusList.map((status) => status.account));
        groupNameToAccountsMap.set(groupName, [...accountSet]);
      });
      setGroupNameToAccountsMap(groupNameToAccountsMap);
    } catch (error) {
      message.error(error.message ? error.message : String(error));
    }
  }

  const fetchTradeEventLogs = async () => {
    try {
      setLoadingData(true);
      const elAdminEventLogs = await Service.fetchEventLogs(selectedTradeAccounts, rangePicker[0], rangePicker[1]);
      const filteredLogs = elAdminEventLogs.filter((log) =>
        ["START_TRADE", "PAUSE_TRADE", "EMPTY_POSITION", "SET_LEVERAGE", "SET_ACCOUNT_PERMISSION"].includes(log.action)
      );
      filteredLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setEventLogs(filteredLogs);
    } catch (error) {
      message.error(error.message ? error.message : String(error));
    } finally {
      setLoadingData(false);
    }
  };

  const fetchAccountEventLogs = async () => {
    try {
      setLoadingData(true);
      const accountLogs = await Service.fetchEventLogs(selectedTradeAccounts, rangePicker[0], rangePicker[1]);
      const filteredLogs = accountLogs.filter((log) =>
        ["NEW_ACCOUNT", "NEW_SESSION", "DELETE_ACCOUNT", "UPDATE_API_KEYS"].includes(log.action)
      );
      filteredLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setEventLogs(filteredLogs);
    } catch (error) {
      message.error(error.message ? error.message : String(error));
    } finally {
      setLoadingData(false);
    }
  };

  const fetchModelEvents = async () => {
    const adminUser = await ElAdminEventLogService.getElAdminUser();
    setLoadingData(true);
    try {
      const actions = ["ENABLE_MODEL", "DISABLE_MODEL"];
      const modelLogs = await Service.fetchEventLogsByActions(rangePicker[0], rangePicker[1], adminUser, actions);
      setEventLogs(modelLogs);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFetchLogs = (tab) => {
    switch (tab) {
      case "1":
        return fetchTradeEventLogs();
      case "2":
        return fetchAccountEventLogs();
      case "3":
        return fetchModelEvents();
      default:
        setEventLogs([]);
    }
  };

  const onTabChange = (key) => {
    setEventLogs([]);
    setActiveTab(key);
    return handleFetchLogs(key);
  };

  const onUpdate = () => {
    return handleFetchLogs(activeTab);
  };

  useEffect(() => {
    fetchTradeStatusAndPermissions();
  }, []);

  const items = [
    { label: "Trade Events", key: "1", children: <EventLogsTable eventLogs={eventLogs} /> },
    { label: "Account Events", key: "2", children: <AccountEventLogsTable eventLogs={eventLogs} /> },
    { label: "Model Events", key: "3", children: <ModelEventLogsTable eventLogs={eventLogs} /> },
  ];

  return (
    <div className={styles.eventLogs}>
      <div className={styles.header}>
        <div className={styles.title}>Event Logs</div>
        <Space>
          <Cascader
            style={{ width: 600 }}
            multiple
            maxTagCount="responsive"
            onChange={(value, selectedOptions) => {
              const accounts = selectedOptions
                .map((arr) => {
                  // if all accounts are selected, the array only has one element.
                  // otherwise, the array has selected element on index 1.
                  if (arr.length === 1) {
                    return arr[0].children.map((element) => element.value);
                  } else if (arr.length === 2) {
                    return arr[1].value;
                  }
                  return undefined;
                })
                .flat();
              setSelectedTradeAccounts(accounts);
            }}
            options={Array.from(groupNameToAccountsMap.keys()).map((groupName) => ({
              label: groupName,
              value: groupName,
              children: groupNameToAccountsMap.get(groupName).map((tradeAccount) => ({ label: tradeAccount, value: tradeAccount })),
            }))}
          />
          <RangePicker
            style={{ marginLeft: 20 }}
            value={rangePicker}
            onChange={(e) => {
              setRangePicker(e);
            }}
          />
          <Button type="primary" style={{ marginLeft: 20 }} onClick={onUpdate} loading={isLoadingData}>
            Update
          </Button>
        </Space>
      </div>
      <Tabs items={items} defaultActiveKey="1" onChange={onTabChange} />
    </div>
  );
};

export default EventLogs;
